import type { FaqSection } from '~/graphql/generated/graphql';

import { faqSectionRouteSegment } from '~/utils/faq/faqSectionLabelKeys';

import { CONTENT_ROUTE_LOGIN } from './content';
import { EDUCATORS_ROUTE_LOGIN } from './educators';

// Unified routes — served from the site root. A `pages:extend` hook in nuxt.config.ts
// strips the `/unified` prefix from pages/unified/* so they resolve at these root paths.
export const UNIFIED_ROUTE_LOGIN = '/login';
export const UNIFIED_ROUTE_ONBOARDING = '/onboarding';
// Student terms-completion surface (mirrors /educators/accept-terms). Reachable
// without a school role so a pending lesson-join can be finished there. The
// 00.auth-route-protection and 03.cluster-terms-acceptance middlewares are a
// matched pair around this path, so both must reference this single constant.
export const UNIFIED_ROUTE_ACCEPT_TERMS = '/accept-terms';
// Every page under it is a sign-in in progress: the social and provider callback pages
// (`/auth/callback`, `/auth/{provider}/callback`) and the provider login failure page.
export const UNIFIED_ROUTE_AUTH = '/auth';
export const UNIFIED_ROUTE_AUTH_FAILED = '/auth/failed';
// Where a public sign-up lands when its email belongs to a school account.
export const UNIFIED_ROUTE_AUTH_SCHOOL_ACCOUNT = '/auth/school-account';
export const UNIFIED_ROUTE_SIGN_IN_QUESTIONS = '/sign-in-questions';
export const UNIFIED_ROUTE_AUTH_PROOF = (provider: string) => `/auth/${provider}/proof`;
export const UNIFIED_ROUTE_INDEX = '/';
export const UNIFIED_ROUTE_START = '/start';
export const UNIFIED_ROUTE_ACCOUNT_DELETED = '/account-deleted';
export const UNIFIED_ROUTE_CDP = '/cdp';
export const UNIFIED_ROUTE_CDP_SHARE_SETTINGS = '/cdp/share-settings';
export const UNIFIED_ROUTE_REFERRALS = '/rewards/referrals';
export const UNIFIED_ROUTE_REFERRALS_FAQ = '/rewards/referrals/faq';
export const UNIFIED_ROUTE_FAQ = '/faq/general';
export const UNIFIED_ROUTE_FAQ_SECTION = (section: FaqSection) => `/faq/${faqSectionRouteSegment(section)}`;
export const UNIFIED_ROUTE_LEARN = '/learn';
export const UNIFIED_ROUTE_LEARN_ASSET = (slug: string) => `/learn/asset/${slug}`;
export const UNIFIED_ROUTE_LEARN_EXPLORE = '/learn/explore';
// The new information architecture's Learn index: the same path, which serves the IA page while
// ia-enabled is on. The IA names are aliases until the flag goes (CP-12117).
export const UNIFIED_ROUTE_LEARN_IA = UNIFIED_ROUTE_LEARN;
// A badge's public page, which guests can read before signing up to earn it.
export const UNIFIED_ROUTE_LEARN_IA_BADGE = (slug: string) => `${UNIFIED_ROUTE_LEARN_IA}/badges/${slug}`;
export const UNIFIED_ROUTE_LEARN_COLLECTION = (slug: string) => `/learn/${slug}`;
export const UNIFIED_ROUTE_ABOUT = '/about';
export const UNIFIED_ROUTE_TERMS = '/terms';
export const UNIFIED_ROUTE_PRIVACY_POLICY = '/privacy-policy';
export const UNIFIED_ROUTE_CAREERS = '/career-advice';
export const UNIFIED_ROUTE_INSTITUTION = '/institutions';
export const UNIFIED_ROUTE_CAREERS_INDUSTRY = (slug: string) => `${UNIFIED_ROUTE_CAREERS}/${slug}`;
export const UNIFIED_ROUTE_CAREER_ADVICE_ARTICLE = ({ considerationAreaSlug, articleSlug }: { considerationAreaSlug: string; articleSlug: string }) => `${UNIFIED_ROUTE_CAREERS}/${considerationAreaSlug}/${articleSlug}`;

export const UNIFIED_ROUTE_ARTICLE_ROOT = '/articles';
export const UNIFIED_ROUTE_ARTICLE = (articleSlug: string) => `${UNIFIED_ROUTE_ARTICLE_ROOT}/${articleSlug}`;

// An article lives under its first consideration area's slug, or at /articles/:slug when it has
// no slugged area.
export interface ArticlePublicPathInput {
  slug: string;
  considerationAreas?: { slug?: string | null }[] | null;
}
export const articlePublicPath = (article: ArticlePublicPathInput): string => {
  const considerationAreaSlug = article.considerationAreas?.[0]?.slug;
  return considerationAreaSlug ? UNIFIED_ROUTE_CAREER_ADVICE_ARTICLE({ considerationAreaSlug, articleSlug: article.slug }) : UNIFIED_ROUTE_ARTICLE(article.slug);
};
export const UNIFIED_ROUTE_INSTITUTION_DETAIL = (slug: string) => `${UNIFIED_ROUTE_INSTITUTION}/${slug}`;
// The Explore index: the IA's entry point to the directories (industries, institutions, and in time jobs, courses and scholarships).
export const UNIFIED_ROUTE_EXPLORE = '/explore';
export const UNIFIED_ROUTE_JOB_DETAIL = '/jobs';
export const UNIFIED_ROUTE_COURSE = '/courses';
// A course at one institution; the id is the course + institution pair's, from the course index.
export const UNIFIED_ROUTE_COURSE_DETAIL = (id: string) => `${UNIFIED_ROUTE_COURSE}/${id}`;
// The hub shares its URL with the industry page (see pages/unified/career-advice/[slug].vue);
// which one answers is the hubs-enabled flag's call.
export const UNIFIED_ROUTE_HUB_DETAIL = (considerationAreaSlug: string) => `${UNIFIED_ROUTE_CAREERS}/${considerationAreaSlug}`;
export const UNIFIED_ROUTE_VIDEOS = '/videos';
export const UNIFIED_ROUTE_VIDEO_DETAIL = ({ slug, hubSlug }: { slug: string; hubSlug?: string }) =>
  `${UNIFIED_ROUTE_VIDEOS}/${slug}${hubSlug ? `?hub=${encodeURIComponent(hubSlug)}` : ''}`;
export const UNIFIED_ROUTE_EDUCATORS = '/educators';
export const UNIFIED_ROUTE_LEADERS = '/leaders';
export const UNIFIED_ROUTE_ADVISORS = '/advisors';
export const UNIFIED_ROUTE_REWARDS = '/rewards';
export const UNIFIED_ROUTE_REQUEST_DEMO = '/request-a-demo';
export const UNIFIED_ROUTE_ACTIVE_CAMPAIGN_TERMS_CONDITIONS = (id: string) => `/campaigns/${id}/terms-conditions`;

export interface GeoMirroredPublicRoute {
  path: string;
  /**
   * Emit cross-cluster `<link rel="alternate" hreflang>` (plugins/03.seo.ts).
   * Requires the content to be region-equivalent, not just the path to resolve.
   */
  hreflang: boolean;
}

// Single source of truth for public routes whose path exists in every cluster
// (file-based, present in every deploy). The geo region switcher
// (useRegionSwitcher) carries any listed path across a region switch; only
// `hreflang: true` entries additionally declare cross-cluster alternates.
// Dynamic, DB-backed content has independent per-region slugs — never list it.
export const GEO_MIRRORED_PUBLIC_ROUTES: readonly GeoMirroredPublicRoute[] = [
  { path: UNIFIED_ROUTE_INDEX, hreflang: true },
  { path: UNIFIED_ROUTE_ABOUT, hreflang: true },
  { path: UNIFIED_ROUTE_FAQ, hreflang: true },
  { path: UNIFIED_ROUTE_LEARN, hreflang: true },
  // Same path, different legal document per entity (AU Year13 Pty Ltd / US Year13 Inc).
  { path: UNIFIED_ROUTE_TERMS, hreflang: false },
  { path: UNIFIED_ROUTE_PRIVACY_POLICY, hreflang: false },
  // The hub path itself is identical across clusters (both render the region's career
  // library at /career-advice), so it declares reciprocal alternates. Its /career-advice/*
  // children do NOT — they have independently-editable per-region slugs (e.g. US
  // "engineering-advanced-manufacturing" vs AU "engineering-manufacturing"), so a naive
  // alternate would point at a sibling URL that 404s. The exact-path gate in plugins/03.seo.ts
  // means flipping this entry only affects the hub, never the children.
  { path: UNIFIED_ROUTE_CAREERS, hreflang: true },
  // Portal login screens — auth surfaces, not SEO content, so no hreflang.
  { path: UNIFIED_ROUTE_LOGIN, hreflang: false },
  { path: EDUCATORS_ROUTE_LOGIN, hreflang: false },
  { path: CONTENT_ROUTE_LOGIN, hreflang: false },
];

// Derived hreflang view — the content-equivalent subset plugins/03.seo.ts gates on.
export const HREFLANG_ALTERNATE_ROUTES: readonly string[] = GEO_MIRRORED_PUBLIC_ROUTES
  .filter(route => route.hreflang)
  .map(route => route.path);

/** Whether a (prefix-free) path exists in every cluster — the region-switch carry-over test. */
export const isGeoMirroredPath = (path: string): boolean =>
  GEO_MIRRORED_PUBLIC_ROUTES.some(mirrored => mirrored.path === path);

// The schools product has no SEO surface beyond the landing page (everything
// else is login-walled), so on schools clusters the hreflang allowlist
// collapses to the landing pair: schools root ↔ schools /au.
export const SCHOOLS_HREFLANG_ALTERNATE_ROUTES: readonly string[] = [UNIFIED_ROUTE_INDEX];
